import { useCallback, useEffect, useRef, useState } from "react";

const MOBILE_MEDIA_QUERY = "(max-width: 767px)";
const SCROLL_CONTAINER_SELECTOR = ".app-container";
const COLLAPSE_BODY_CLASS = "priceme-mobile-header-collapsed";
const COLLAPSE_AFTER_SCROLL_PX = 56;
const EXPAND_NEAR_TOP_OFFSET_PX = 100;
const RECOLLAPSE_AFTER_SCROLL_DOWN_PX = 48;

type UseMobileHeaderScrollCollapseOptions = {
    isEnabled: boolean;
    isOverlayOpen?: boolean;
};

type UseMobileHeaderScrollCollapseResult = {
    isCollapsed: boolean;
    expandHeader: () => void;
};

/**
 * Collapses the PriceMe header on mobile while scrolling down and restores it near the top or on tap.
 */
export default function useMobileHeaderScrollCollapse({
    isEnabled,
    isOverlayOpen = false,
}: UseMobileHeaderScrollCollapseOptions): UseMobileHeaderScrollCollapseResult {
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [isMobile, setIsMobile] = useState<boolean>(() =>
        typeof window !== "undefined" ? window.matchMedia(MOBILE_MEDIA_QUERY).matches : false,
    );
    const isCollapsedRef = useRef<boolean>(false);
    const isManualExpandRef = useRef<boolean>(false);
    const manualExpandScrollTopRef = useRef<number>(0);

    const setCollapsed = useCallback((collapsed: boolean): void => {
        if (isCollapsedRef.current === collapsed) {
            return;
        }
        isCollapsedRef.current = collapsed;
        setIsCollapsed(collapsed);
        if (collapsed) {
            document.body.classList.add(COLLAPSE_BODY_CLASS);
            return;
        }
        document.body.classList.remove(COLLAPSE_BODY_CLASS);
    }, []);

    const expandHeader = useCallback((): void => {
        if (!isMobile) {
            return;
        }
        const container = document.querySelector<HTMLElement>(SCROLL_CONTAINER_SELECTOR);
        manualExpandScrollTopRef.current = container?.scrollTop ?? 0;
        isManualExpandRef.current = true;
        setCollapsed(false);
    }, [isMobile, setCollapsed]);

    useEffect(() => {
        const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
        const syncMobile = (): void => {
            setIsMobile(mediaQuery.matches);
            if (!mediaQuery.matches) {
                isManualExpandRef.current = false;
                setCollapsed(false);
            }
        };
        syncMobile();
        mediaQuery.addEventListener("change", syncMobile);
        return () => {
            mediaQuery.removeEventListener("change", syncMobile);
            document.body.classList.remove(COLLAPSE_BODY_CLASS);
        };
    }, [setCollapsed]);

    useEffect(() => {
        if (!isEnabled || !isMobile || isOverlayOpen) {
            if (isOverlayOpen && isCollapsedRef.current) {
                isManualExpandRef.current = false;
                setCollapsed(false);
            }
            return;
        }
        const container = document.querySelector<HTMLElement>(SCROLL_CONTAINER_SELECTOR);
        if (!container) {
            return;
        }
        const handleScroll = (): void => {
            const scrollTop = container.scrollTop;
            if (scrollTop <= EXPAND_NEAR_TOP_OFFSET_PX) {
                isManualExpandRef.current = false;
                setCollapsed(false);
                return;
            }
            if (isManualExpandRef.current) {
                if (scrollTop >= manualExpandScrollTopRef.current + RECOLLAPSE_AFTER_SCROLL_DOWN_PX) {
                    isManualExpandRef.current = false;
                    setCollapsed(true);
                }
                return;
            }
            if (scrollTop >= COLLAPSE_AFTER_SCROLL_PX) {
                setCollapsed(true);
            }
        };
        container.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();
        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, [isEnabled, isMobile, isOverlayOpen, setCollapsed]);

    return { isCollapsed, expandHeader };
}
